import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Package, Tag, TrendingDown, ShoppingBag, Menu, Store, LogOut, Palette, Layers, Star,
  FolderTree, Users, Settings, Megaphone, Boxes, Sparkles, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BeeLogo } from './Navbar';

// Sidebar sections. `label` also drives the data-testid used by tests.
const NAV_SECTIONS = [
  { items: [{ path: '/admin', icon: LayoutDashboard, label: 'Dashboard' }] },
  { title: 'Customers & Orders', items: [
    { path: '/admin/orders', icon: ShoppingBag, label: 'Orders' },
    { path: '/admin/customers', icon: Users, label: 'Customers' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
  ] },
  { title: 'Catalog', items: [
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/categories', icon: FolderTree, label: 'Categories' },
    { path: '/admin/collections', icon: Layers, label: 'Collections' },
    { path: '/admin/featured', icon: Sparkles, label: 'Featured' },
    { path: '/admin/bundles', icon: Boxes, label: 'Bundles & Deals' },
  ] },
  { title: 'Marketing', items: [
    { path: '/admin/sales', icon: TrendingDown, label: 'Sales' },
    { path: '/admin/promotions', icon: Megaphone, label: 'Promotions & Ads' },
    { path: '/admin/coupons', icon: Tag, label: 'Coupons' },
  ] },
  { title: 'Storefront', items: [
    { path: '/admin/page-builder', icon: Palette, label: 'Page Builder' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ] },
];

const ALL_ITEMS = NAV_SECTIONS.flatMap(s => s.items);
const isActive = (path, pathname) => (path === '/admin' ? pathname === path : pathname.startsWith(path));

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const current = ALL_ITEMS.find(i => isActive(i.path, location.pathname));
  const section = NAV_SECTIONS.find(s => s.items.includes(current));

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8] text-ink flex">
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-ink/10 z-40 flex flex-col transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="px-5 h-16 flex items-center border-b border-ink/10 flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-2">
            <BeeLogo className="w-8 h-8" />
            <span className="font-black text-lg text-ink leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Market <span className="text-honey-600">@</span> Admin
            </span>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 space-y-5">
          {NAV_SECTIONS.map((sec, i) => (
            <div key={sec.title || i}>
              {sec.title && <p className="px-5 mb-1.5 text-[11px] font-black uppercase tracking-[0.15em] text-ink/40">{sec.title}</p>}
              <div>
                {sec.items.map(({ path, icon: Icon, label }) => {
                  const active = isActive(path, location.pathname);
                  return (
                    <Link key={path} to={path} onClick={() => setSidebarOpen(false)}
                      data-testid={`admin-nav-${label.toLowerCase()}`}
                      aria-current={active ? 'page' : undefined} className="menu-item">
                      <Icon className="w-4 h-4" />
                      {label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="py-3 border-t border-ink/10 flex-shrink-0">
          <Link to="/" className="menu-item">
            <Store className="w-4 h-4" /> View store <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
          </Link>
          <button onClick={handleLogout} className="menu-item menu-item-danger">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-ink/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-ink/10 px-6 h-16 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden text-ink/70" aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
          <p className="text-sm text-ink/50">
            {section?.title && <>{section.title} <span className="mx-1 text-ink/30">/</span></>}
            <span className="text-ink font-semibold">{current?.label || 'Admin'}</span>
          </p>
          {user && <span className="ml-auto text-sm text-ink/55 hidden sm:block">{user.email}</span>}
        </header>
        <main className="p-6">
          <motion.div key={location.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
